import { doc, setDoc, deleteDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

const VAPID_PUBLIC = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

// 'needs-install' = iOS Safari tab: Web Push only works once the app is added to the
// Home Screen, so we must NOT fire requestPermission() there (it silently no-ops).
export type PushState = 'unsupported' | 'needs-install' | 'denied' | 'ready' | 'subscribed';

function isIOS() {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}
export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}
function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function urlBase64ToUint8Array(base64: string) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

// Stable doc id from the endpoint so re-subscribing the same device replaces, not dupes.
function endpointId(endpoint: string) {
  let h = 0;
  for (let i = 0; i < endpoint.length; i++) h = (h * 31 + endpoint.charCodeAt(i)) >>> 0;
  return `sub_${h.toString(36)}`;
}

export async function currentPushState(): Promise<PushState> {
  if (isIOS() && !isStandalone()) return 'needs-install';
  if (!pushSupported() || !VAPID_PUBLIC) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return sub ? 'subscribed' : 'ready';
}

export async function enablePush(uid: string, role: 'owner' | 'lessee') {
  if (!db || !VAPID_PUBLIC) throw new Error('Notifications aren’t configured.');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Notifications permission was not granted.');
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
    });
  }
  await setDoc(doc(db, 'pushSubscriptions', endpointId(sub.endpoint)), {
    uid,
    role,
    subscription: sub.toJSON(),
    createdAt: serverTimestamp(),
  });
}

export async function disablePush() {
  if (!db) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await deleteDoc(doc(db, 'pushSubscriptions', endpointId(sub.endpoint))).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
}

// Fires a single test notification back to this user via the onPushTestCreated trigger.
export async function sendTestPush(uid: string) {
  if (!db) return;
  await addDoc(collection(db, 'pushTests'), { uid, createdAt: serverTimestamp() });
}
